// The web app inside the iPhone app (docs/ios-build-spec.md §6-7). The app
// shows the long-tail pages (Projects, Workflows, Settings …) as "web
// screens": it loads /companion/<page>?embed=ios in a WKWebView and draws
// the frame itself — navigation bar, tabs, back. This module is the web
// side of that: whether this page runs embedded, and the one channel back
// to the app, window.webkit.messageHandlers.lattice (registered by the app
// before the page loads, so it is there from the first script).
//
// The flag is read ONCE, at boot (main.tsx, before React renders), and
// kept in sessionStorage: react-router links drop the query, and a reload
// (chunk recovery, the app reloading a screen) reloads the address without
// it. sessionStorage belongs to one web view, so no browser tab inherits
// it. The query is then taken off the address, so a link copied or opened
// in Safari from a web screen is the ordinary page, with its own shell.
//
// The app's message handler counts as well, query or not: the app
// registers it before any page loads, and some loads never carry the
// query. A web code the server refuses redirects to a bare "/" in a fresh
// web view (nothing remembered yet); judged by the query alone, that page
// was the full web app inside the iPhone app: its login, no signed-out
// message, the update toast, and after signing in there, the phone shell.
// No browser, Safari view or other app registers a "lattice" handler.
//
// The pure parts (resolveEmbed, withoutEmbedParam, nativeHandler,
// tellsNativeSignedOut, showsOffline, retryDelayMs) are self-tested:
// embedded.selftest.ts, run by `npm run check:format`.

import type { SignedOutReason } from "@/stores/auth";

export const EMBED_KEY = "lattice.embed";
export const EMBED_PARAM = "embed";

/** The only host so far. */
export type EmbedHost = "ios";

/**
 * What the web app tells the app. The contract is spec §6:
 *   navigate     an in-page navigation reached a screen the app draws
 *                natively; the app opens it, and the web screen goes back
 *                to where it was (NativeLinkBridge).
 *   signed-out   the login screen is showing because the session check
 *                answered 401 or the user signed out (never for a network
 *                error or a 5xx: tellsNativeSignedOut): the app mints a new
 *                web code once, then shows its own sign-in.
 *   offline      the session check could not reach the server (offline,
 *                a 5xx, a proxy's error page): the page shows its own "Can't
 *                reach Corvus" screen and retries; the app may show its own
 *                retry screen instead. Sent once each time that screen shows.
 *   back-claim   a page took the back control over (an open project);
 *                label null when it gives it back (companion/backLink.ts).
 *   title        optional; not sent yet.
 *   boot         sent once per page load, at boot: this build says
 *                `rendered` when it has drawn, so the app waits for that
 *                (with a long safety net) before it shows the web view. An
 *                app that never hears it is talking to an older build and
 *                shows the page a moment after it loads.
 *   rendered     sent once per page load, when the companion screen has
 *                drawn with its first data (useNativeRendered): the app
 *                keeps its own placeholder over the page until then, so the
 *                page's loading states never show inside the app.
 */
export type NativeMessage =
  | { type: "navigate"; path: string; search: string; hash: string }
  | { type: "signed-out" }
  | { type: "offline" }
  | { type: "back-claim"; label: string | null }
  | { type: "title"; title: string }
  | { type: "boot"; version: number }
  | { type: "rendered" };

/** The `boot` message's version: 1 = this build sends `rendered` and answers `refresh()`. */
export const EMBED_CONTRACT_VERSION = 1;

/** What the app may call on the page (window.latticeNative). */
export interface NativeApi {
  /** Run the page's claimed back action; false when nothing is claimed, so the app goes back itself. */
  back: () => boolean;
  /**
   * Pull to refresh: fetch the screen's data again in place (no reload).
   * Resolves true when done; the app reloads the page when it is absent
   * (an older build) or answers anything but true.
   */
  refresh?: () => Promise<boolean>;
}

interface MessageHandler {
  postMessage: (message: unknown) => void;
}

/**
 * The embed host named by the boot query, else the one this web view
 * remembered, else the app itself when its handler is on the page.
 */
export function resolveEmbed(search: string, stored: string | null, appListening = false): EmbedHost | null {
  if (new URLSearchParams(search).get(EMBED_PARAM) === "ios") return "ios";
  if (stored === "ios") return "ios";
  return appListening ? "ios" : null;
}

/**
 * The query without `embed`, every other parameter byte for byte (no
 * re-encoding through URLSearchParams, which would turn %20 into +).
 */
export function withoutEmbedParam(search: string): string {
  const body = search.startsWith("?") ? search.slice(1) : search;
  if (!body) return "";
  const kept = body.split("&").filter((part) => {
    if (part === "") return false;
    const name = part.split("=", 1)[0];
    let decoded = name;
    try {
      decoded = decodeURIComponent(name.replace(/\+/g, " "));
    } catch {
      /* a malformed name is not ours: keep it */
    }
    return decoded !== EMBED_PARAM;
  });
  return kept.length ? `?${kept.join("&")}` : "";
}

/** The app's message handler on a global scope, or null outside the app. */
export function nativeHandler(scope: unknown): MessageHandler | null {
  const webkit = (scope as { webkit?: { messageHandlers?: Record<string, unknown> } } | null)?.webkit;
  const handler = webkit?.messageHandlers?.lattice as Partial<MessageHandler> | undefined;
  return handler && typeof handler.postMessage === "function" ? (handler as MessageHandler) : null;
}

let host: EmbedHost | null | undefined;

/**
 * Read the flag (boot only; main.tsx calls it before rendering). Returns
 * whether the page is embedded. Later calls return the first answer.
 */
export function initEmbedded(): boolean {
  if (host !== undefined) return host !== null;
  if (typeof window === "undefined") return false;
  let stored: string | null = null;
  try {
    stored = window.sessionStorage.getItem(EMBED_KEY);
  } catch {
    /* storage blocked: the query alone decides, for this page */
  }
  host = resolveEmbed(window.location.search, stored, nativeHandler(window) !== null);
  if (host) {
    // CSS for the app only (index.css: a transparent page the app's own
    // colour shows through, and a document the app's pull-to-refresh can
    // pull), then tell the app this build says when it has drawn.
    document.documentElement.setAttribute("data-embedded", host);
    postToNative({ type: "boot", version: EMBED_CONTRACT_VERSION });
    try {
      window.sessionStorage.setItem(EMBED_KEY, host);
    } catch {
      /* the flag then lasts until the next reload */
    }
    const { pathname, search, hash } = window.location;
    const clean = withoutEmbedParam(search);
    // Before the router exists, so it starts from the clean address; the
    // entry's state is kept (a reload keeps react-router's own).
    if (clean !== search) window.history.replaceState(window.history.state, "", pathname + clean + hash);
  }
  return host !== null;
}

/** True when this page is a web screen inside the iPhone app. */
export function isEmbedded(): boolean {
  return initEmbedded();
}

/** Whether the app is listening (false in any browser, embedded or not). */
export function canPostToNative(): boolean {
  return typeof window !== "undefined" && nativeHandler(window) !== null;
}

/** Send a message to the app; a no-op (false) outside it. */
export function postToNative(message: NativeMessage): boolean {
  const handler = typeof window === "undefined" ? null : nativeHandler(window);
  if (!handler) return false;
  try {
    handler.postMessage(message);
    return true;
  } catch {
    // WebKit throws only for a value it cannot clone; ours are plain JSON.
    return false;
  }
}

/**
 * Whether the login screen tells the app the web view is signed out. Only
 * when it is: the session check answered 401, or the user signed out here.
 * A check that failed any other way (offline, a 5xx, a proxy's error page)
 * says nothing about the session, and the app would re-mint a web code for
 * nothing, then show its sign-in to someone who is signed in.
 */
export function tellsNativeSignedOut(reason: SignedOutReason | null): boolean {
  return reason === "expired" || reason === "logout";
}

/**
 * Whether a signed-out page shows "Can't reach Corvus" instead of the login
 * form: the session check failed without a 401, so the session may be fine
 * and the form would ask someone signed in to sign in again. Every deploy
 * opens this window (one api pod, Recreate), in a browser as in the app.
 */
export function showsOffline(reason: SignedOutReason | null): boolean {
  return reason === "unchecked";
}

// The offline screen's retries: soon at first, then every half minute.
const RETRY_DELAYS_MS = [2_000, 4_000, 8_000, 15_000, 30_000];

/** How long the offline screen waits before its `attempt`-th automatic retry (0-based). */
export function retryDelayMs(attempt: number): number {
  const last = RETRY_DELAYS_MS.length - 1;
  return RETRY_DELAYS_MS[Number.isFinite(attempt) ? Math.min(Math.max(0, Math.floor(attempt)), last) : last];
}

/**
 * Let the app call into the page (`window.latticeNative.back()` from
 * evaluateJavaScript). Merged: main.tsx gives `back` at boot, the embedded
 * shell adds `refresh` once the data layer exists.
 */
export function exposeToNative(api: Partial<NativeApi>): void {
  const scope = window as Window & { latticeNative?: Partial<NativeApi> };
  scope.latticeNative = { ...scope.latticeNative, ...api };
}

// When the first render with data counts as drawn (useNativeRendered): no
// query fetching and no route still loading for a short quiet spell, or
// the cap, so a request that never ends cannot keep the page hidden.
export const RENDER_QUIET_MS = 120;
export const RENDER_CAP_MS = 6_000;

/** Whether the page has drawn: quiet long enough, or past the cap. */
export function renderSettled(elapsedMs: number, busy: boolean, quietMs: number): boolean {
  if (elapsedMs >= RENDER_CAP_MS) return true;
  return !busy && quietMs >= RENDER_QUIET_MS;
}
